import { Badge } from "./Badge";
import type {
  LeadStatus,
  LeadQualificationDecision,
  CallOutcome,
  ConversationStatus,
  SubscriptionStatus,
} from "@platform/shared";

/**
 * Single source of truth for how each domain status renders. As of Step 12
 * the stored lead statuses are exactly the product wording
 * (`new | contacted | qualified | won | lost`), so labels are just the
 * capitalized value.
 */

const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  won: "Won",
  lost: "Lost",
};

const LEAD_STATUS_TONE: Record<LeadStatus, "neutral" | "signal" | "success"> = {
  new: "signal",
  contacted: "neutral",
  qualified: "success",
  won: "success",
  lost: "neutral",
};

export function LeadStatusBadge({ status }: { status: LeadStatus }) {
  return <Badge tone={LEAD_STATUS_TONE[status]}>{LEAD_STATUS_LABEL[status]}</Badge>;
}

const QUALIFICATION_LABEL: Record<LeadQualificationDecision, string> = {
  qualified: "AI: qualified",
  unqualified: "AI: not a fit",
  needs_more_info: "AI: gathering info",
  needs_human: "Needs review",
};

/** The qualification *decision* (distinct from the pipeline status). A
 * `needs_human` decision is the one that asks something of the user, so
 * it's the only one drawn in the attention colour. */
export function QualificationBadge({ decision }: { decision: LeadQualificationDecision | null }) {
  if (!decision) return <span className="text-ink-soft">—</span>;
  const tone = decision === "needs_human" ? "signal" : decision === "qualified" ? "success" : "neutral";
  return <Badge tone={tone}>{QUALIFICATION_LABEL[decision]}</Badge>;
}

export const LEAD_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = (
  ["new", "contacted", "qualified", "won", "lost"] as LeadStatus[]
).map((value) => ({ value, label: LEAD_STATUS_LABEL[value] }));

const CALL_OUTCOME_LABEL: Record<CallOutcome, string> = {
  missed: "Missed",
  answered: "Answered",
  voicemail: "Voicemail",
  failed: "Failed",
};

const CALL_OUTCOME_TONE: Record<CallOutcome, "neutral" | "signal" | "success"> = {
  missed: "signal",
  answered: "success",
  voicemail: "neutral",
  failed: "signal",
};

export function CallOutcomeBadge({ outcome }: { outcome: CallOutcome }) {
  return <Badge tone={CALL_OUTCOME_TONE[outcome]}>{CALL_OUTCOME_LABEL[outcome]}</Badge>;
}

const CONVERSATION_STATUS_LABEL: Record<ConversationStatus, string> = {
  active: "Active",
  closed: "Closed",
};

export function ConversationStatusBadge({ status }: { status: ConversationStatus }) {
  return <Badge tone={status === "active" ? "signal" : "neutral"}>{CONVERSATION_STATUS_LABEL[status]}</Badge>;
}

const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: "Trialing",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
};

const SUBSCRIPTION_STATUS_TONE: Record<SubscriptionStatus, "neutral" | "signal" | "success"> = {
  trialing: "neutral",
  active: "success",
  // The one billing state that asks something of the customer.
  past_due: "signal",
  canceled: "neutral",
};

export function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus }) {
  return <Badge tone={SUBSCRIPTION_STATUS_TONE[status]}>{SUBSCRIPTION_STATUS_LABEL[status]}</Badge>;
}
